/**
 * world/room_gen.ts — Zone Room Generation
 *
 * Generates a procedural Room for a given WorldZone. Each zone type produces
 * a distinct tile layout, wall configuration, elevation terrain, and furniture
 * set that reflects the zone's character — a forest zone gets grass tiles with
 * dirt paths and trees, a harbor gets dock planks over water, a mountain gets
 * rocky crags with elevation shifts.
 *
 * Uses a deterministic hash seeded by zone.id so the same zone always produces
 * the same room layout (until we add persistence).
 */

import type { Room, TileCell, WallSegment, FurniturePlacement, TileType, WallType, Direction } from "../engine/state.js";
import type { WorldZone } from "./zones.js";

// ─── Deterministic hash for stable procedural generation ─────────────────────

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return h;
}

/** Seeded pseudo-random — returns a function that yields 0–1 values */
function seededRng(seed: number): () => number {
  let s = seed | 0;
  if (s === 0) s = 1;
  return () => {
    s ^= s << 13;
    s ^= s >> 17;
    s ^= s << 5;
    return ((s >>> 0) / 4294967296);
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

let _furnitureCounter = 0;
function fid(prefix: string): string {
  return `${prefix}_${_furnitureCounter++}`;
}

function makeTile(type: TileType, elevation = 0, walkable = true): TileCell {
  return { type, elevation, walkable };
}

function addFurniture(
  list: FurniturePlacement[],
  type: string,
  tileX: number,
  tileY: number,
  state: Record<string, unknown> = {},
  direction: Direction = "S",
  elevation = 0,
): void {
  list.push({
    id: fid(type),
    type,
    tileX,
    tileY,
    elevation,
    direction,
    state,
  });
}

// ─── Zone Room Generators ────────────────────────────────────────────────────

function generateEmberLane(rng: () => number): Room {
  const W = 12, H = 10;
  const tiles: TileCell[][] = [];

  for (let y = 0; y < H; y++) {
    tiles[y] = [];
    for (let x = 0; x < W; x++) {
      // Default: wood boardwalk for buildings on sides
      tiles[y][x] = makeTile("wood");
    }
  }

  // Cobblestone main path — center lane
  for (let y = 0; y < H; y++) {
    for (let x = 4; x <= 7; x++) {
      tiles[y][x] = makeTile("cobblestone");
    }
  }

  // Water stream cutting across rows 4-5, columns 0-3 and 8-11
  // The stream runs under a cobblestone bridge in the center
  for (let x = 0; x < W; x++) {
    if (x >= 4 && x <= 7) {
      // Bridge over the stream — cobblestone at elevation 1
      tiles[4][x] = makeTile("cobblestone", 1);
      tiles[5][x] = makeTile("cobblestone", 1);
    } else {
      // Water stream on both sides
      tiles[4][x] = makeTile("water", 0, false);
      tiles[5][x] = makeTile("water", 0, false);
    }
  }

  // Grass flower patches along edges with some variation
  for (let y = 0; y < H; y++) {
    if (rng() > 0.35) tiles[y][0] = makeTile("grass");
    if (rng() > 0.35) tiles[y][W - 1] = makeTile("grass");
    if (rng() > 0.65) tiles[y][1] = makeTile("grass");
    if (rng() > 0.65) tiles[y][W - 2] = makeTile("grass");
  }
  // Grass banks along the stream
  for (let x = 0; x < 4; x++) {
    if (tiles[3][x].type === "wood") tiles[3][x] = makeTile("grass");
    if (tiles[6][x].type === "wood") tiles[6][x] = makeTile("grass");
  }
  for (let x = 8; x < W; x++) {
    if (tiles[3][x].type === "wood") tiles[3][x] = makeTile("grass");
    if (tiles[6][x].type === "wood") tiles[6][x] = makeTile("grass");
  }

  // North wall — plaster storefronts
  const walls: WallSegment[] = [];
  for (let x = 0; x < W; x++) {
    if (x < 4 || x > 7) {
      walls.push({ x, y: 0, side: "N", type: "plaster", hasWindow: rng() > 0.4, hasDoor: false });
    }
  }
  // West wall
  for (let y = 0; y < H; y++) {
    walls.push({ x: 0, y, side: "W", type: "plaster", hasWindow: y === 2 || y === 7, hasDoor: y === H - 1 });
  }

  const furniture: FurniturePlacement[] = [];
  // Lamppost line along the cobblestone path
  addFurniture(furniture, "floor_lamp", 4, 1, { on: true, intensity: 0.7 }, "SE");
  addFurniture(furniture, "floor_lamp", 7, 1, { on: true, intensity: 0.7 }, "SW");
  addFurniture(furniture, "floor_lamp", 4, 8, { on: true, intensity: 0.6 }, "NE");
  addFurniture(furniture, "floor_lamp", 7, 8, { on: true, intensity: 0.6 }, "NW");
  // Bench by the stream — a place to sit and watch the water
  addFurniture(furniture, "bench", 3, 3, {}, "S");
  addFurniture(furniture, "bench", 8, 6, {}, "N");
  // Cozy building furniture on left side
  addFurniture(furniture, "sofa_oak", 1, 1, {}, "E");
  addFurniture(furniture, "coffee_table", 2, 2, {}, "S");
  // Right-side building: a little reading nook
  addFurniture(furniture, "bookshelf", 10, 1, { books: 18 }, "W");
  addFurniture(furniture, "chair_wooden", 9, 2, {}, "E");
  addFurniture(furniture, "candle", 10, 2, { lit: true }, "S");
  // Flower pots on the grass edges
  addFurniture(furniture, "potted_plant", 0, 2, { growthStage: 3, waterLevel: 0.9 }, "SE");
  addFurniture(furniture, "potted_plant", 11, 7, { growthStage: 4, waterLevel: 0.8 }, "SW");
  addFurniture(furniture, "potted_plant", 0, 8, { growthStage: 2, waterLevel: 0.7 }, "SE");

  return buildRoom("zone_ember_lane", "Ember Lane", W, H, tiles, walls, furniture, {
    warmth: 0.9, energy: 0.4, mystery: 0.15, nature: 0.25,
  });
}

function generateHearthstoneSquare(rng: () => number): Room {
  const W = 14, H = 14;
  const tiles: TileCell[][] = [];

  for (let y = 0; y < H; y++) {
    tiles[y] = [];
    for (let x = 0; x < W; x++) {
      const dx = x - W / 2 + 0.5;
      const dy = y - H / 2 + 0.5;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < 2.5) {
        // Elevated marble fountain platform
        tiles[y][x] = makeTile("marble", 1);
      } else if (dist < 4) {
        // Inner walkway ring — marble at elevation 1 (stairs up from cobble)
        tiles[y][x] = makeTile("marble", 1);
      } else if (dist < 5.5) {
        // Cobblestone transition ring — ground level
        tiles[y][x] = makeTile("cobblestone");
      } else {
        tiles[y][x] = makeTile(rng() > 0.25 ? "cobblestone" : "grass");
      }
    }
  }

  // Water fountain tiles on the elevated platform (non-walkable)
  tiles[5][6] = makeTile("water", 1, false);
  tiles[5][7] = makeTile("water", 1, false);
  tiles[6][6] = makeTile("water", 1, false);
  tiles[6][7] = makeTile("water", 1, false);
  // Marble edge around the fountain pool
  tiles[4][6] = makeTile("marble", 1);
  tiles[4][7] = makeTile("marble", 1);
  tiles[7][6] = makeTile("marble", 1);
  tiles[7][7] = makeTile("marble", 1);

  // Ensure center spawn is walkable (center is 7,7 — on marble elevated ring)
  tiles[7][7] = makeTile("marble", 1);

  // Stepped approach ramps (elevation transitions on cardinal directions)
  // South approach — gentle stairs from ground to platform
  for (let x = 5; x <= 8; x++) {
    tiles[9][x] = makeTile("marble", 0);  // base of stairs
    tiles[8][x] = makeTile("marble", 1);  // top of stairs
  }
  // North approach
  for (let x = 5; x <= 8; x++) {
    tiles[3][x] = makeTile("marble", 1);
    tiles[2][x] = makeTile("marble", 0);
  }

  const walls: WallSegment[] = [];
  const furniture: FurniturePlacement[] = [];

  // Benches facing the fountain — ground level ring
  addFurniture(furniture, "bench", 3, 5, {}, "E");
  addFurniture(furniture, "bench", 3, 8, {}, "E");
  addFurniture(furniture, "bench", 10, 5, {}, "W");
  addFurniture(furniture, "bench", 10, 8, {}, "W");
  // Benches on the outer ring — conversational pairs
  addFurniture(furniture, "bench", 1, 3, {}, "SE");
  addFurniture(furniture, "bench", 12, 3, {}, "SW");
  addFurniture(furniture, "bench", 1, 10, {}, "NE");
  addFurniture(furniture, "bench", 12, 10, {}, "NW");

  // Dining tables for outdoor eating — social furniture
  addFurniture(furniture, "dining_table", 2, 6, {}, "S");
  addFurniture(furniture, "chair_wooden", 1, 6, {}, "E");
  addFurniture(furniture, "chair_wooden", 2, 5, {}, "S");
  addFurniture(furniture, "dining_table", 11, 6, {}, "S");
  addFurniture(furniture, "chair_wooden", 12, 6, {}, "W");
  addFurniture(furniture, "chair_wooden", 11, 5, {}, "S");

  // Corner street lamps
  addFurniture(furniture, "floor_lamp", 0, 0, { on: true, intensity: 0.6 }, "SE");
  addFurniture(furniture, "floor_lamp", 13, 0, { on: true, intensity: 0.6 }, "SW");
  addFurniture(furniture, "floor_lamp", 0, 13, { on: true, intensity: 0.6 }, "NE");
  addFurniture(furniture, "floor_lamp", 13, 13, { on: true, intensity: 0.6 }, "NW");
  // Inner lamps on the platform
  addFurniture(furniture, "floor_lamp", 4, 4, { on: true, intensity: 0.5 }, "SE", 1);
  addFurniture(furniture, "floor_lamp", 9, 4, { on: true, intensity: 0.5 }, "SW", 1);

  // Greenery
  addFurniture(furniture, "potted_plant", 3, 2, { growthStage: 4, waterLevel: 1 }, "S");
  addFurniture(furniture, "potted_plant", 10, 2, { growthStage: 3, waterLevel: 0.9 }, "S");
  addFurniture(furniture, "potted_plant", 3, 11, { growthStage: 3, waterLevel: 0.8 }, "S");
  addFurniture(furniture, "potted_plant", 10, 11, { growthStage: 4, waterLevel: 1 }, "S");

  return buildRoom("zone_hearthstone_square", "Hearthstone Square", W, H, tiles, walls, furniture, {
    warmth: 0.8, energy: 0.75, mystery: 0.1, nature: 0.2,
  });
}

function generateLanternMarket(rng: () => number): Room {
  const W = 14, H = 12;
  const tiles: TileCell[][] = [];

  for (let y = 0; y < H; y++) {
    tiles[y] = [];
    for (let x = 0; x < W; x++) {
      // Default marble walkway for main aisles
      if (x >= 5 && x <= 8) {
        tiles[y][x] = makeTile("marble");
      } else {
        tiles[y][x] = makeTile("cobblestone");
      }
    }
  }

  // Elevated merchant stall platforms (wood at elevation 1)
  // Left stall block: columns 0-3, rows 1-3
  for (let y = 1; y <= 3; y++) {
    for (let x = 0; x <= 3; x++) {
      tiles[y][x] = makeTile("wood", 1);
    }
  }
  // Left stall block: columns 0-3, rows 5-7
  for (let y = 5; y <= 7; y++) {
    for (let x = 0; x <= 3; x++) {
      tiles[y][x] = makeTile("wood", 1);
    }
  }
  // Right stall block: columns 10-13, rows 1-3
  for (let y = 1; y <= 3; y++) {
    for (let x = 10; x <= 13; x++) {
      tiles[y][x] = makeTile("wood", 1);
    }
  }
  // Right stall block: columns 10-13, rows 5-7
  for (let y = 5; y <= 7; y++) {
    for (let x = 10; x <= 13; x++) {
      tiles[y][x] = makeTile("wood", 1);
    }
  }
  // Bottom stall: columns 1-4, rows 9-10
  for (let y = 9; y <= 10; y++) {
    for (let x = 1; x <= 4; x++) {
      tiles[y][x] = makeTile("wood", 1);
    }
  }

  // Transition step tiles (cobblestone at elevation 0 adjacent to platforms)
  // These are already at elevation 0 so the step is implicit

  const walls: WallSegment[] = [];
  // Glass shop fronts on the north wall
  for (let x = 0; x < W; x++) {
    if (x < 4 || x > 9) {
      walls.push({ x, y: 0, side: "N", type: "glass", hasWindow: true, hasDoor: false });
    }
  }
  // Glass walls on left side stalls
  for (let y = 1; y <= 3; y++) {
    walls.push({ x: 0, y, side: "W", type: "glass", hasWindow: true, hasDoor: false });
  }
  for (let y = 5; y <= 7; y++) {
    walls.push({ x: 0, y, side: "W", type: "glass", hasWindow: true, hasDoor: false });
  }
  // Remaining west wall
  walls.push({ x: 0, y: 0, side: "W", type: "glass", hasWindow: false, hasDoor: false });
  walls.push({ x: 0, y: 4, side: "W", type: "glass", hasWindow: false, hasDoor: false });
  for (let y = 8; y < H; y++) {
    walls.push({ x: 0, y, side: "W", type: "glass", hasWindow: y === 9, hasDoor: y === H - 1 });
  }

  const furniture: FurniturePlacement[] = [];

  // Left stall 1 (rows 1-3): Book merchant
  addFurniture(furniture, "bookshelf", 0, 1, { books: 24 }, "E", 1);
  addFurniture(furniture, "bookshelf", 0, 2, { books: 18 }, "E", 1);
  addFurniture(furniture, "desk_study", 2, 2, { hasComputer: false }, "S", 1);
  addFurniture(furniture, "candle", 3, 1, { lit: true }, "S", 1);

  // Left stall 2 (rows 5-7): Curiosity shop
  addFurniture(furniture, "crystal_ball", 1, 5, {}, "S", 1);
  addFurniture(furniture, "bookshelf", 0, 6, { books: 12 }, "E", 1);
  addFurniture(furniture, "globe", 3, 5, {}, "S", 1);
  addFurniture(furniture, "candle", 2, 7, { lit: true }, "S", 1);

  // Right stall 1 (rows 1-3): Music shop
  addFurniture(furniture, "record_player", 11, 1, { playing: true }, "W", 1);
  addFurniture(furniture, "jukebox", 13, 2, {}, "W", 1);
  addFurniture(furniture, "desk_study", 11, 3, { hasComputer: false }, "W", 1);

  // Right stall 2 (rows 5-7): Furnishings display
  addFurniture(furniture, "coffee_table", 11, 5, {}, "S", 1);
  addFurniture(furniture, "chair_wooden", 12, 6, {}, "W", 1);
  addFurniture(furniture, "candle", 13, 5, { lit: true }, "S", 1);
  addFurniture(furniture, "rug_round", 11, 6, {}, "S", 1);

  // Bottom stall: Arcade/games
  addFurniture(furniture, "arcade_cabinet", 2, 9, {}, "S", 1);
  addFurniture(furniture, "arcade_cabinet", 4, 9, {}, "S", 1);

  // Central aisle lanterns — hanging from posts along the marble walkway
  addFurniture(furniture, "floor_lamp", 5, 0, { on: true, intensity: 0.8 }, "S");
  addFurniture(furniture, "floor_lamp", 8, 0, { on: true, intensity: 0.8 }, "S");
  addFurniture(furniture, "floor_lamp", 5, 4, { on: true, intensity: 0.7 }, "S");
  addFurniture(furniture, "floor_lamp", 8, 4, { on: true, intensity: 0.7 }, "S");
  addFurniture(furniture, "floor_lamp", 5, 8, { on: true, intensity: 0.6 }, "S");
  addFurniture(furniture, "floor_lamp", 8, 8, { on: true, intensity: 0.6 }, "S");
  addFurniture(furniture, "floor_lamp", 6, 11, { on: true, intensity: 0.7 }, "S");

  // Decorative rugs in the center aisle
  addFurniture(furniture, "rug_round", 6, 3, {}, "S");
  addFurniture(furniture, "rug_round", 7, 7, {}, "S");

  return buildRoom("zone_lantern_market", "Lantern Market", W, H, tiles, walls, furniture, {
    warmth: 0.7, energy: 0.9, mystery: 0.2, nature: 0.05,
  });
}

function generateVerdantPark(rng: () => number): Room {
  const W = 16, H = 16;
  const tiles: TileCell[][] = [];

  // Build terrain with a gentle hill rising toward the northwest
  for (let y = 0; y < H; y++) {
    tiles[y] = [];
    for (let x = 0; x < W; x++) {
      // Hill center at (4, 4) — elevation gradient radiating outward
      const hdx = x - 4, hdy = y - 4;
      const hillDist = Math.sqrt(hdx * hdx + hdy * hdy);
      let elev = 0;
      if (hillDist < 2.5) {
        elev = 2;
      } else if (hillDist < 4.5) {
        elev = 1;
      }
      tiles[y][x] = makeTile("grass", elev);
    }
  }

  // Winding dirt path from south entrance up the hill and across the park
  const pathPoints: [number, number][] = [];
  for (let y = H - 1; y >= 0; y--) {
    const pathX = Math.floor(W / 2 + Math.sin(y * 0.65) * 3);
    pathPoints.push([pathX, y]);
    tiles[y][pathX] = makeTile("dirt", tiles[y][pathX].elevation);
    if (pathX + 1 < W) tiles[y][pathX + 1] = makeTile("dirt", tiles[y][pathX + 1].elevation);
  }
  // Branch path going west up the hill
  for (let x = 8; x >= 3; x--) {
    const by = 6 + Math.floor(Math.sin(x * 0.8) * 0.5);
    if (by >= 0 && by < H) {
      tiles[by][x] = makeTile("dirt", tiles[by][x].elevation);
    }
  }

  // Pond in the southeast — water at elevation 0 with dock tiles at edge
  for (let y = 10; y < 14; y++) {
    for (let x = 10; x < 14; x++) {
      const pdx = x - 12, pdy = y - 12;
      if (pdx * pdx + pdy * pdy < 4) {
        tiles[y][x] = makeTile("water", 0, false);
      }
    }
  }
  // Dock tiles at pond edge — walkable viewing spots
  tiles[10][10] = makeTile("dock", 0);
  tiles[10][11] = makeTile("dock", 0);
  tiles[11][10] = makeTile("dock", 0);
  tiles[9][12] = makeTile("dock", 0);
  tiles[9][11] = makeTile("dock", 0);

  const walls: WallSegment[] = [];
  const furniture: FurniturePlacement[] = [];

  // Trees at various growth stages scattered across the park
  const treeSpots: [number, number, number, number][] = [
    // [x, y, growthStage, waterLevel]
    [1, 1, 4, 1.0],
    [3, 2, 3, 0.9],  // on the hill
    [6, 1, 4, 1.0],
    [14, 2, 4, 0.9],
    [0, 6, 3, 0.8],
    [14, 5, 4, 1.0],
    [1, 10, 4, 0.9],
    [5, 14, 3, 0.7],
    [8, 12, 4, 1.0],
    [14, 14, 2, 0.6],
    [0, 14, 4, 0.8],
  ];
  for (const [tx, ty, growth, water] of treeSpots) {
    if (tx < W && ty < H && tiles[ty][tx].walkable && tiles[ty][tx].type !== "dirt" && tiles[ty][tx].type !== "dock") {
      addFurniture(furniture, "potted_plant", tx, ty, { growthStage: growth, waterLevel: water }, "S", tiles[ty][tx].elevation);
    }
  }

  // Hilltop summit — a bench and a globe overlooking the park
  addFurniture(furniture, "bench", 4, 3, {}, "SE", 2);
  addFurniture(furniture, "globe", 5, 4, {}, "S", 1);

  // Park benches along the path
  addFurniture(furniture, "bench", 9, 5, {}, "S");
  addFurniture(furniture, "bench", 7, 9, {}, "E");
  // Bench overlooking the pond
  addFurniture(furniture, "bench", 9, 11, {}, "SE");

  // Lamps along the main path
  addFurniture(furniture, "floor_lamp", 8, 2, { on: true, intensity: 0.4 }, "S");
  addFurniture(furniture, "floor_lamp", 7, 7, { on: true, intensity: 0.4 }, "S");
  addFurniture(furniture, "floor_lamp", 9, 13, { on: true, intensity: 0.4 }, "S");

  // Fish tank near the pond (a small aquarium display)
  addFurniture(furniture, "fish_tank", 10, 9, { fishCount: 5 }, "S");

  return buildRoom("zone_verdant_park", "Verdant Park", W, H, tiles, walls, furniture, {
    warmth: 0.55, energy: 0.45, mystery: 0.3, nature: 0.95,
  });
}

function generateMoonlitHarbor(rng: () => number): Room {
  const W = 14, H = 12;
  const tiles: TileCell[][] = [];

  for (let y = 0; y < H; y++) {
    tiles[y] = [];
    for (let x = 0; x < W; x++) {
      if (y < 3) {
        // Deep sea — non-walkable water
        tiles[y][x] = makeTile("water", 0, false);
      } else if (y < 5) {
        // Dock planks at elevation 1 — the working pier
        tiles[y][x] = makeTile("dock", 1);
      } else if (y < 7) {
        // Stone quay at elevation 2 — the elevated wharf
        tiles[y][x] = makeTile("stone", 2);
      } else {
        // Cobblestone harbor road at elevation 2
        tiles[y][x] = makeTile("cobblestone", 2);
      }
    }
  }

  // Sand beach strip where water meets dock
  for (let x = 0; x < W; x++) {
    if (rng() > 0.3) {
      tiles[2][x] = makeTile("sand", 0, true);
    }
  }

  // Extended pier jutting into the water (dock at elev 1, extending over water)
  for (let y = 0; y < 3; y++) {
    tiles[y][6] = makeTile("dock", 1);
    tiles[y][7] = makeTile("dock", 1);
  }

  // Lighthouse platform — elevated stone at corner (elevation 3)
  tiles[7][12] = makeTile("stone", 3);
  tiles[7][13] = makeTile("stone", 3);
  tiles[8][12] = makeTile("stone", 3);
  tiles[8][13] = makeTile("stone", 3);
  // Steps up to lighthouse
  tiles[9][12] = makeTile("stone", 2);
  tiles[9][13] = makeTile("stone", 2);

  // Ensure center tile is walkable (center is 7,6 — on stone quay at elev 2)
  tiles[6][7] = makeTile("stone", 2);

  const walls: WallSegment[] = [];
  const furniture: FurniturePlacement[] = [];

  // Lighthouse lamp at the top
  addFurniture(furniture, "floor_lamp", 12, 7, { on: true, intensity: 1.0 }, "S", 3);
  addFurniture(furniture, "telescope", 13, 8, {}, "N", 3);

  // Cargo crates stacked on the quay (bookshelves as crates)
  addFurniture(furniture, "bookshelf", 1, 5, { books: 0 }, "SE", 2);
  addFurniture(furniture, "bookshelf", 2, 5, { books: 0 }, "SE", 2);
  addFurniture(furniture, "bookshelf", 3, 6, { books: 0 }, "S", 2);
  addFurniture(furniture, "bookshelf", 10, 5, { books: 0 }, "SW", 2);
  addFurniture(furniture, "bookshelf", 11, 6, { books: 0 }, "S", 2);

  // Harbor dock lamps
  addFurniture(furniture, "floor_lamp", 0, 3, { on: true, intensity: 0.5 }, "SE", 1);
  addFurniture(furniture, "floor_lamp", 5, 3, { on: true, intensity: 0.6 }, "S", 1);
  addFurniture(furniture, "floor_lamp", 9, 3, { on: true, intensity: 0.5 }, "S", 1);
  addFurniture(furniture, "floor_lamp", 13, 3, { on: true, intensity: 0.5 }, "SW", 1);

  // Wharf street lamps
  addFurniture(furniture, "floor_lamp", 4, 9, { on: true, intensity: 0.4 }, "S", 2);
  addFurniture(furniture, "floor_lamp", 9, 9, { on: true, intensity: 0.4 }, "S", 2);

  // Bench on the pier for watching the sea
  addFurniture(furniture, "bench", 6, 0, {}, "S", 1);

  // Fisherman's table on the dock
  addFurniture(furniture, "coffee_table", 3, 4, {}, "S", 1);
  addFurniture(furniture, "chair_wooden", 2, 4, {}, "E", 1);

  // Record player on the quay — harbor tunes
  addFurniture(furniture, "record_player", 7, 10, { playing: true }, "S", 2);

  return buildRoom("zone_moonlit_harbor", "Moonlit Harbor", W, H, tiles, walls, furniture, {
    warmth: 0.35, energy: 0.6, mystery: 0.5, nature: 0.4,
  });
}

function generateIronforgeDistrict(rng: () => number): Room {
  const W = 12, H = 12;
  const tiles: TileCell[][] = [];

  for (let y = 0; y < H; y++) {
    tiles[y] = [];
    for (let x = 0; x < W; x++) {
      // Dark stone throughout as the forge base
      tiles[y][x] = makeTile("stone_dark");
    }
  }

  // Forge platforms at elevation 1 surrounding the central work area
  // North forge platform
  for (let y = 0; y < 3; y++) {
    for (let x = 0; x < W; x++) {
      tiles[y][x] = makeTile("stone_dark", 1);
    }
  }
  // East forge platform
  for (let y = 3; y < 9; y++) {
    for (let x = 9; x < W; x++) {
      tiles[y][x] = makeTile("stone_dark", 1);
    }
  }
  // West forge platform
  for (let y = 3; y < 9; y++) {
    for (let x = 0; x < 3; x++) {
      tiles[y][x] = makeTile("stone_dark", 1);
    }
  }
  // South forge platform
  for (let y = 9; y < H; y++) {
    for (let x = 0; x < W; x++) {
      tiles[y][x] = makeTile("stone_dark", 1);
    }
  }

  // Master smith's elevated platform (elev 2, NW corner)
  tiles[0][0] = makeTile("stone", 2);
  tiles[0][1] = makeTile("stone", 2);
  tiles[1][0] = makeTile("stone", 2);
  tiles[1][1] = makeTile("stone", 2);

  // Central work pit at elevation 0 (already set)
  // Lava channels cutting through the center of the pit
  // Horizontal lava channel
  for (let x = 3; x < 9; x++) {
    tiles[5][x] = makeTile("lava", 0, false);
  }
  // Vertical lava channel
  for (let y = 3; y < 9; y++) {
    tiles[y][6] = makeTile("lava", 0, false);
  }
  // Walkable stone bridges over the lava cross
  tiles[5][4] = makeTile("stone", 0);
  tiles[5][7] = makeTile("stone", 0);
  tiles[4][6] = makeTile("stone", 0);
  tiles[7][6] = makeTile("stone", 0);

  // Accent stone tiles in the work pit
  for (let y = 3; y < 9; y++) {
    for (let x = 3; x < 9; x++) {
      if (tiles[y][x].type === "stone_dark" && rng() > 0.7) {
        tiles[y][x] = makeTile("stone");
      }
    }
  }

  // Ensure center spawn tile (6,6) is walkable — it's on the lava channel
  // Move spawn safety to (5,6) which is already stone_dark at elev 0
  tiles[6][5] = makeTile("stone_dark");

  const walls: WallSegment[] = [];
  // Heavy brick walls
  for (let x = 0; x < W; x++) {
    walls.push({ x, y: 0, side: "N", type: "brick", hasWindow: x === 3 || x === 8, hasDoor: false });
  }
  for (let y = 0; y < H; y++) {
    walls.push({ x: 0, y, side: "W", type: "brick", hasWindow: y === 4 || y === 8, hasDoor: y === H - 1 });
  }

  const furniture: FurniturePlacement[] = [];

  // Multiple forges (fireplaces) around the perimeter platforms
  addFurniture(furniture, "fireplace", 4, 1, { lit: true }, "S", 1);
  addFurniture(furniture, "fireplace", 8, 1, { lit: true }, "S", 1);
  addFurniture(furniture, "fireplace", 1, 5, { lit: true }, "E", 1);
  addFurniture(furniture, "fireplace", 10, 5, { lit: true }, "W", 1);

  // Anvils (desks) on the forge platforms
  addFurniture(furniture, "desk_study", 2, 0, { hasComputer: false }, "S", 1);
  addFurniture(furniture, "desk_study", 6, 0, { hasComputer: false }, "S", 1);
  addFurniture(furniture, "desk_study", 10, 0, { hasComputer: false }, "S", 1);
  addFurniture(furniture, "desk_study", 0, 7, { hasComputer: false }, "E", 1);
  addFurniture(furniture, "desk_study", 11, 7, { hasComputer: false }, "W", 1);

  // Tool racks (bookshelves) along walls
  addFurniture(furniture, "bookshelf", 0, 3, { books: 8 }, "E", 1);
  addFurniture(furniture, "bookshelf", 11, 3, { books: 10 }, "W", 1);
  addFurniture(furniture, "bookshelf", 3, 10, { books: 6 }, "S", 1);
  addFurniture(furniture, "bookshelf", 8, 10, { books: 12 }, "S", 1);

  // Master smith's area
  addFurniture(furniture, "desk_study", 0, 0, { hasComputer: false }, "SE", 2);
  addFurniture(furniture, "candle", 1, 0, { lit: true }, "S", 2);

  // Dramatic lighting — orange forge glow
  addFurniture(furniture, "floor_lamp", 3, 3, { on: true, intensity: 0.9 }, "SE");
  addFurniture(furniture, "floor_lamp", 8, 3, { on: true, intensity: 0.8 }, "SW");
  addFurniture(furniture, "floor_lamp", 3, 8, { on: true, intensity: 0.7 }, "NE");
  addFurniture(furniture, "floor_lamp", 8, 8, { on: true, intensity: 0.7 }, "NW");

  return buildRoom("zone_ironforge_district", "Ironforge District", W, H, tiles, walls, furniture, {
    warmth: 0.65, energy: 0.85, mystery: 0.1, nature: 0.05,
  });
}

function generateWhisperwood(rng: () => number): Room {
  const W = 18, H = 18;
  const tiles: TileCell[][] = [];

  for (let y = 0; y < H; y++) {
    tiles[y] = [];
    for (let x = 0; x < W; x++) {
      // Forest floor with gentle undulating elevation
      const nx = Math.sin(x * 0.5 + y * 0.3) * 0.5 + Math.cos(y * 0.4) * 0.5;
      const elev = nx > 0.4 ? 1 : 0;
      tiles[y][x] = makeTile("grass", elev);
    }
  }

  // Central clearing at elevation 0 — a circular open space
  for (let y = 6; y < 12; y++) {
    for (let x = 7; x < 12; x++) {
      const cdx = x - 9.5, cdy = y - 9;
      if (cdx * cdx + cdy * cdy < 7) {
        tiles[y][x] = makeTile("grass", 0);
      }
    }
  }
  // Mossy stone circle in the clearing center (ancient ritual site)
  tiles[8][9] = makeTile("stone", 0);
  tiles[8][10] = makeTile("stone", 0);
  tiles[9][9] = makeTile("stone", 0);
  tiles[9][10] = makeTile("stone", 0);

  // Winding dirt path from south to clearing, then branching north
  for (let y = H - 1; y >= 0; y--) {
    const pathX = Math.floor(9 + Math.sin(y * 0.6) * 3);
    if (pathX >= 0 && pathX < W) {
      tiles[y][pathX] = makeTile("dirt", tiles[y][pathX].elevation);
    }
    if (pathX + 1 < W) {
      tiles[y][pathX + 1] = makeTile("dirt", tiles[y][pathX + 1].elevation);
    }
  }

  // Small forest stream (west side)
  for (let y = 12; y < 17; y++) {
    const streamX = 2 + Math.floor(Math.sin(y * 0.7) * 1.5);
    if (streamX >= 0 && streamX < W) {
      tiles[y][streamX] = makeTile("water", 0, false);
      if (streamX + 1 < W) tiles[y][streamX + 1] = makeTile("water", 0, false);
    }
  }
  // Stepping stone crossing over the stream
  tiles[14][3] = makeTile("stone", 0);

  // Raised terrain around the clearing edges (elev 1, creating a natural bowl)
  for (let y = 5; y < 13; y++) {
    for (let x = 5; x < 14; x++) {
      const cdx = x - 9.5, cdy = y - 9;
      const d = Math.sqrt(cdx * cdx + cdy * cdy);
      if (d >= 2.6 && d < 4 && tiles[y][x].type === "grass") {
        tiles[y][x].elevation = 1;
      }
    }
  }

  const walls: WallSegment[] = [];
  const furniture: FurniturePlacement[] = [];

  // Dense tree placement — avoiding paths and the clearing
  const treePositions: [number, number][] = [
    [0, 0], [3, 0], [6, 0], [12, 0], [15, 0], [17, 1],
    [0, 3], [4, 2], [14, 2], [17, 3],
    [1, 5], [5, 5], [13, 4], [16, 5],
    [0, 7], [4, 7], [14, 6], [17, 7],
    [0, 9], [5, 10], [13, 10], [17, 10],
    [1, 12], [6, 13], [13, 13], [16, 12],
    [0, 15], [5, 15], [12, 15], [17, 15],
    [1, 17], [4, 17], [8, 17], [14, 17], [17, 17],
  ];
  for (const [x, y] of treePositions) {
    if (x < W && y < H && tiles[y][x].type !== "water" && tiles[y][x].type !== "dirt" && tiles[y][x].type !== "stone") {
      addFurniture(furniture, "potted_plant", x, y, {
        growthStage: 3 + Math.floor(rng() * 2),
        waterLevel: 0.7 + rng() * 0.3,
      }, "S", tiles[y][x].elevation);
    }
  }

  // Secret: Ancient crystal ball hidden in the clearing center (the ritual stone)
  addFurniture(furniture, "crystal_ball", 9, 8, {}, "S");

  // Candles around the stone circle — someone has been here
  addFurniture(furniture, "candle", 8, 7, { lit: true }, "S");
  addFurniture(furniture, "candle", 11, 8, { lit: true }, "S");
  addFurniture(furniture, "candle", 8, 10, { lit: true }, "S");

  // A lonely bench in the clearing
  addFurniture(furniture, "bench", 10, 10, {}, "NW");

  // Faint lamps along the path (could be fireflies)
  addFurniture(furniture, "floor_lamp", 9, 1, { on: true, intensity: 0.2 }, "S", tiles[1][9].elevation);
  addFurniture(furniture, "floor_lamp", 8, 16, { on: true, intensity: 0.2 }, "S", tiles[16][8].elevation);

  return buildRoom("zone_whisperwood", "Whisperwood", W, H, tiles, walls, furniture, {
    warmth: 0.25, energy: 0.3, mystery: 0.8, nature: 0.9,
  });
}

function generateAshenPeaks(rng: () => number): Room {
  const W = 16, H = 16;
  const tiles: TileCell[][] = [];

  for (let y = 0; y < H; y++) {
    tiles[y] = [];
    for (let x = 0; x < W; x++) {
      tiles[y][x] = makeTile("stone");
    }
  }

  // Mountain mass — summit in the north-center with elevation radiating outward
  // Summit at approximately (8, 2)
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const dx = x - 8;
      const dy = y - 2;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 3) {
        tiles[y][x].elevation = 3;
      } else if (dist < 5) {
        tiles[y][x].elevation = 2;
      } else if (dist < 7.5) {
        tiles[y][x].elevation = 1;
      }
      // Snow on high elevations
      if (tiles[y][x].elevation >= 2) {
        tiles[y][x].type = "snow";
      }
    }
  }

  // Spiraling path up the mountain — a stone stairway
  // Start at the south (ground level), wind counterclockwise up
  // South approach (elev 0 -> 1)
  for (let x = 6; x <= 10; x++) {
    tiles[H - 2][x] = makeTile("stone", 0);
    tiles[H - 3][x] = makeTile("stone", 0);
  }
  // East side ascent (elev 0 -> 1)
  for (let y = H - 4; y >= 8; y--) {
    tiles[y][11] = makeTile("stone", 1);
    tiles[y][12] = makeTile("stone", 1);
  }
  // North-east turn (elev 1 -> 2)
  for (let x = 10; x >= 5; x--) {
    tiles[7][x] = makeTile("stone", 1);
  }
  // West side ascent (elev 1 -> 2)
  for (let y = 7; y >= 4; y--) {
    tiles[y][4] = makeTile("stone", 2);
    tiles[y][5] = makeTile("stone", 2);
    tiles[y][4].type = "snow";
    tiles[y][5].type = "snow";
  }
  // Final summit approach (elev 2 -> 3)
  for (let x = 5; x <= 9; x++) {
    tiles[3][x] = makeTile("snow", 2);
  }
  tiles[2][7] = makeTile("snow", 3);
  tiles[2][8] = makeTile("snow", 3);
  tiles[2][9] = makeTile("snow", 3);

  // Dirt path at the base
  for (let x = 2; x < W - 2; x++) {
    tiles[H - 1][x] = makeTile("dirt");
  }

  // Rocky outcrops scattered on lower slopes
  for (let i = 0; i < 6; i++) {
    const rx = Math.floor(rng() * W);
    const ry = Math.floor(rng() * H);
    if (tiles[ry][rx].elevation === 0 && ry > 8) {
      tiles[ry][rx].elevation = 1;
      tiles[ry][rx].walkable = false;
    }
  }

  // Ensure center (8,8) is walkable
  tiles[8][8] = makeTile("stone", 1);

  const walls: WallSegment[] = [];
  const furniture: FurniturePlacement[] = [];

  // Summit campfire — the reward for climbing
  addFurniture(furniture, "fireplace", 8, 2, { lit: true }, "S", 3);
  // Telescope at the summit — scanning the horizon
  addFurniture(furniture, "telescope", 7, 1, {}, "N", 3);

  // Base camp
  addFurniture(furniture, "fireplace", 8, H - 3, { lit: true }, "S");
  addFurniture(furniture, "bench", 6, H - 3, {}, "E");
  addFurniture(furniture, "bench", 10, H - 3, {}, "W");
  // Supplies at base camp
  addFurniture(furniture, "bookshelf", 5, H - 2, { books: 0 }, "S"); // supply crate
  addFurniture(furniture, "coffee_table", 11, H - 2, {}, "S");

  // Wind-bent trees at lower elevation
  addFurniture(furniture, "potted_plant", 2, H - 3, { growthStage: 2, waterLevel: 0.4 }, "S");
  addFurniture(furniture, "potted_plant", 13, H - 3, { growthStage: 2, waterLevel: 0.3 }, "S");
  addFurniture(furniture, "potted_plant", 1, 10, { growthStage: 1, waterLevel: 0.2 }, "S");
  addFurniture(furniture, "potted_plant", 14, 10, { growthStage: 1, waterLevel: 0.3 }, "S");

  // Trail markers (candles as cairns)
  addFurniture(furniture, "candle", 11, 10, { lit: true }, "S", 1);
  addFurniture(furniture, "candle", 12, 8, { lit: true }, "S", 1);
  addFurniture(furniture, "candle", 10, 7, { lit: true }, "S", 1);
  addFurniture(furniture, "candle", 5, 5, { lit: true }, "S", 2);

  // Lamp at base
  addFurniture(furniture, "floor_lamp", 7, H - 2, { on: true, intensity: 0.3 }, "S");

  return buildRoom("zone_ashen_peaks", "Ashen Peaks", W, H, tiles, walls, furniture, {
    warmth: 0.1, energy: 0.5, mystery: 0.55, nature: 0.7,
  });
}

function generateRoothollow(rng: () => number): Room {
  const W = 14, H = 14;
  const tiles: TileCell[][] = [];

  // Underground cavern — stone_dark floor throughout
  for (let y = 0; y < H; y++) {
    tiles[y] = [];
    for (let x = 0; x < W; x++) {
      tiles[y][x] = makeTile("stone_dark");
    }
  }

  // Crystal formation pillars — elevated, non-walkable (stalactite/stalagmite columns)
  const crystalPillars: [number, number, number][] = [
    [1, 1, 3], [3, 3, 2], [11, 2, 3], [12, 5, 2],
    [0, 8, 3], [4, 10, 2], [10, 11, 3], [13, 9, 2],
    [6, 1, 2], [1, 12, 2],
  ];
  for (const [cx, cy, elev] of crystalPillars) {
    if (cx < W && cy < H) {
      tiles[cy][cx] = makeTile("crystal", elev, false);
      // Cluster — adjacent crystal tiles at lower elevation
      if (cx + 1 < W && rng() > 0.3) {
        tiles[cy][cx + 1] = makeTile("crystal", Math.max(1, elev - 1), false);
      }
      if (cy + 1 < H && rng() > 0.4) {
        tiles[cy + 1][cx] = makeTile("crystal", Math.max(1, elev - 1), false);
      }
    }
  }

  // Glowing crystal pool — water tiles surrounded by crystal
  // Pool in the upper-right area
  for (let y = 4; y < 7; y++) {
    for (let x = 8; x < 11; x++) {
      tiles[y][x] = makeTile("water", 0, false);
    }
  }
  // Crystal rim around the pool
  for (let x = 7; x <= 11; x++) {
    if (tiles[3][x].walkable) tiles[3][x] = makeTile("crystal", 1, true);
    if (tiles[7][x].walkable) tiles[7][x] = makeTile("crystal", 1, true);
  }
  for (let y = 4; y < 7; y++) {
    if (tiles[y][7].walkable) tiles[y][7] = makeTile("crystal", 1, true);
    if (tiles[y][11].walkable) tiles[y][11] = makeTile("crystal", 1, true);
  }

  // Underground river cutting diagonally through the south
  for (let i = 0; i < 10; i++) {
    const rx = 1 + i;
    const ry = 10 + Math.floor(i * 0.3);
    if (rx < W && ry < H) {
      tiles[ry][rx] = makeTile("water", 0, false);
    }
  }
  // Stone bridge over the underground river
  tiles[10][5] = makeTile("stone_dark", 1);
  tiles[10][6] = makeTile("stone_dark", 1);
  tiles[11][7] = makeTile("stone_dark", 1);

  // Walkable crystal floor patches — luminescent ground
  const crystalFloor: [number, number][] = [
    [5, 5], [6, 6], [7, 8], [3, 7], [9, 9],
  ];
  for (const [cx, cy] of crystalFloor) {
    if (cx < W && cy < H && tiles[cy][cx].walkable) {
      tiles[cy][cx] = makeTile("crystal");
    }
  }

  // Ensure center spawn (7,7) is walkable
  tiles[7][7] = makeTile("stone_dark");

  const walls: WallSegment[] = [];
  for (let x = 0; x < W; x++) {
    walls.push({ x, y: 0, side: "N", type: "stone_dark", hasWindow: false, hasDoor: false });
  }
  for (let y = 0; y < H; y++) {
    walls.push({ x: 0, y, side: "W", type: "stone_dark", hasWindow: false, hasDoor: y === H - 1 });
  }

  const furniture: FurniturePlacement[] = [];

  // Bioluminescent lamps — very dim, cave ambiance
  addFurniture(furniture, "floor_lamp", 5, 3, { on: true, intensity: 0.2 }, "S");
  addFurniture(furniture, "floor_lamp", 12, 3, { on: true, intensity: 0.15 }, "SW");
  addFurniture(furniture, "floor_lamp", 7, 7, { on: true, intensity: 0.25 }, "S");
  addFurniture(furniture, "floor_lamp", 2, 9, { on: true, intensity: 0.15 }, "SE");
  addFurniture(furniture, "floor_lamp", 12, 12, { on: true, intensity: 0.1 }, "NW");

  // Crystal balls near the pool — amplifying the glow
  addFurniture(furniture, "crystal_ball", 8, 3, {}, "S", 1);
  addFurniture(furniture, "crystal_ball", 11, 7, {}, "S", 1);

  // Secret alcove in the SE corner — a hidden study
  addFurniture(furniture, "desk_study", 12, 13, { hasComputer: false }, "W");
  addFurniture(furniture, "bookshelf", 13, 12, { books: 6 }, "W");
  addFurniture(furniture, "candle", 13, 13, { lit: true }, "S");

  // Ancient artifact on a crystal pedestal near the pool
  addFurniture(furniture, "globe", 9, 3, {}, "S", 1);

  return buildRoom("zone_roothollow", "Roothollow", W, H, tiles, walls, furniture, {
    warmth: 0.2, energy: 0.2, mystery: 0.95, nature: 0.15,
  });
}

function generateSkyreachTerrace(rng: () => number): Room {
  const W = 14, H = 12;
  const tiles: TileCell[][] = [];

  // Three distinct tiers with marble stairways connecting them
  for (let y = 0; y < H; y++) {
    tiles[y] = [];
    for (let x = 0; x < W; x++) {
      if (y < 3) {
        // Top tier — observatory level
        tiles[y][x] = makeTile("marble", 3);
      } else if (y < 5) {
        // Upper-mid tier
        tiles[y][x] = makeTile("marble", 2);
      } else if (y < 8) {
        // Lower-mid tier
        tiles[y][x] = makeTile("marble", 1);
      } else {
        // Ground level
        tiles[y][x] = makeTile("marble", 0);
      }
    }
  }

  // Marble stairways connecting the tiers (centered, 4 tiles wide)
  // Stair from ground (0) to lower-mid (1): row 7
  for (let x = 5; x <= 8; x++) {
    tiles[7][x] = makeTile("marble", 1);
  }
  // Stair from lower-mid (1) to upper-mid (2): row 4
  for (let x = 5; x <= 8; x++) {
    tiles[4][x] = makeTile("marble", 2);
  }
  // Stair from upper-mid (2) to top (3): row 2
  for (let x = 5; x <= 8; x++) {
    tiles[2][x] = makeTile("marble", 3);
  }

  // Observatory dome footprint at the top (crystal floor)
  for (let y = 0; y < 2; y++) {
    for (let x = 4; x <= 9; x++) {
      tiles[y][x] = makeTile("crystal", 3);
    }
  }

  const walls: WallSegment[] = [];
  // Glass railings at tier edges (safety barriers)
  for (let x = 0; x < 5; x++) {
    walls.push({ x, y: 0, side: "N", type: "glass", hasWindow: true, hasDoor: false });
  }
  for (let x = 10; x < W; x++) {
    walls.push({ x, y: 0, side: "N", type: "glass", hasWindow: true, hasDoor: false });
  }
  // Crystal dome walls at the top
  for (let x = 4; x <= 9; x++) {
    walls.push({ x, y: 0, side: "N", type: "crystal", hasWindow: true, hasDoor: false });
  }
  // Glass railings along edges of upper tiers
  for (let y = 0; y < 5; y++) {
    walls.push({ x: 0, y, side: "W", type: "glass", hasWindow: true, hasDoor: false });
  }
  for (let y = 5; y < H; y++) {
    walls.push({ x: 0, y, side: "W", type: "glass", hasWindow: y === 6, hasDoor: y === H - 1 });
  }

  const furniture: FurniturePlacement[] = [];

  // Observatory dome — telescope and study materials at the summit
  addFurniture(furniture, "telescope", 6, 0, {}, "N", 3);
  addFurniture(furniture, "bookshelf", 4, 0, { books: 30 }, "E", 3);
  addFurniture(furniture, "bookshelf", 9, 0, { books: 28 }, "W", 3);
  addFurniture(furniture, "desk_study", 7, 1, { hasComputer: false }, "N", 3);
  addFurniture(furniture, "globe", 5, 1, {}, "S", 3);

  // Upper terrace — contemplation area
  addFurniture(furniture, "crystal_ball", 2, 3, {}, "S", 2);
  addFurniture(furniture, "sofa_oak", 10, 3, {}, "W", 2);
  addFurniture(furniture, "candle", 12, 3, { lit: true }, "S", 2);
  addFurniture(furniture, "floor_lamp", 1, 4, { on: true, intensity: 0.5 }, "SE", 2);
  addFurniture(furniture, "floor_lamp", 12, 4, { on: true, intensity: 0.5 }, "SW", 2);

  // Mid terrace — social lounge
  addFurniture(furniture, "sofa_oak", 3, 5, {}, "E", 1);
  addFurniture(furniture, "coffee_table", 4, 6, {}, "S", 1);
  addFurniture(furniture, "sofa_oak", 10, 6, {}, "W", 1);
  addFurniture(furniture, "floor_lamp", 1, 6, { on: true, intensity: 0.4 }, "SE", 1);
  addFurniture(furniture, "floor_lamp", 12, 6, { on: true, intensity: 0.4 }, "SW", 1);
  addFurniture(furniture, "potted_plant", 0, 5, { growthStage: 3, waterLevel: 0.6 }, "S", 1);

  // Ground level — welcoming area
  addFurniture(furniture, "bench", 3, 9, {}, "SE");
  addFurniture(furniture, "bench", 10, 9, {}, "SW");
  addFurniture(furniture, "rug_round", 7, 9, {}, "S");
  addFurniture(furniture, "potted_plant", 0, 8, { growthStage: 2, waterLevel: 0.5 }, "S");
  addFurniture(furniture, "potted_plant", 13, 8, { growthStage: 2, waterLevel: 0.5 }, "S");
  addFurniture(furniture, "floor_lamp", 2, 10, { on: true, intensity: 0.3 }, "SE");
  addFurniture(furniture, "floor_lamp", 11, 10, { on: true, intensity: 0.3 }, "SW");

  return buildRoom("zone_skyreach_terrace", "Skyreach Terrace", W, H, tiles, walls, furniture, {
    warmth: 0.15, energy: 0.7, mystery: 0.4, nature: 0.6,
  });
}

function generateCrystalCaverns(rng: () => number): Room {
  const W = 16, H = 16;
  const tiles: TileCell[][] = [];

  // Base layer: stone_dark cavern floor at elevation 2 (the walkable ring)
  for (let y = 0; y < H; y++) {
    tiles[y] = [];
    for (let x = 0; x < W; x++) {
      tiles[y][x] = makeTile("stone_dark", 2);
    }
  }

  // Deep central pit — lava lake at elevation 0
  for (let y = 5; y < 11; y++) {
    for (let x = 5; x < 11; x++) {
      const pdx = x - 8, pdy = y - 8;
      if (pdx * pdx + pdy * pdy < 12) {
        tiles[y][x] = makeTile("lava", 0, false);
      }
    }
  }

  // Crystal bridges spanning the pit (1-tile wide, walkable, elevation 2)
  // North-south bridge
  for (let y = 4; y < 12; y++) {
    tiles[y][8] = makeTile("crystal", 2);
  }
  // East-west bridge
  for (let x = 4; x < 12; x++) {
    tiles[8][x] = makeTile("crystal", 2);
  }
  // Bridge intersection
  tiles[8][8] = makeTile("crystal", 2);

  // Crystal stalactites — high elevation non-walkable pillars
  const stalactites: [number, number][] = [
    [1, 1], [2, 4], [13, 2], [14, 6],
    [0, 10], [3, 13], [12, 14], [14, 11],
    [1, 7], [13, 8],
  ];
  for (const [sx, sy] of stalactites) {
    if (sx < W && sy < H) {
      tiles[sy][sx] = makeTile("crystal", 3, false);
    }
  }

  // Outer ring crystal floor accents
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      if (tiles[y][x].type === "stone_dark" && tiles[y][x].walkable) {
        if ((x + y * 3) % 7 < 2 || rng() > 0.75) {
          tiles[y][x].type = "crystal";
        }
      }
    }
  }

  // Hidden alcove in the NE corner — a secret room
  tiles[0][13] = makeTile("crystal", 2);
  tiles[0][14] = makeTile("crystal", 2);
  tiles[1][13] = makeTile("crystal", 2);
  tiles[1][14] = makeTile("crystal", 2);
  tiles[0][15] = makeTile("crystal", 2);
  tiles[1][15] = makeTile("crystal", 2);

  // Ensure center (8,8) is walkable — it's the bridge intersection
  tiles[8][8] = makeTile("crystal", 2);

  const walls: WallSegment[] = [];
  for (let x = 0; x < W; x++) {
    walls.push({ x, y: 0, side: "N", type: "crystal", hasWindow: false, hasDoor: false });
  }
  for (let y = 0; y < H; y++) {
    walls.push({ x: 0, y, side: "W", type: "crystal", hasWindow: false, hasDoor: y === H - 1 });
  }

  const furniture: FurniturePlacement[] = [];

  // Eerie crystal lighting — dim glow from the crystal formations
  addFurniture(furniture, "floor_lamp", 3, 2, { on: true, intensity: 0.25 }, "SE", 2);
  addFurniture(furniture, "floor_lamp", 12, 2, { on: true, intensity: 0.25 }, "SW", 2);
  addFurniture(furniture, "floor_lamp", 3, 12, { on: true, intensity: 0.2 }, "NE", 2);
  addFurniture(furniture, "floor_lamp", 12, 12, { on: true, intensity: 0.2 }, "NW", 2);

  // Bridge center — a crystal nexus point
  addFurniture(furniture, "crystal_ball", 8, 7, {}, "S", 2);

  // Secret alcove treasure — hidden study of a lost explorer
  addFurniture(furniture, "desk_study", 14, 0, { hasComputer: false }, "S", 2);
  addFurniture(furniture, "bookshelf", 15, 0, { books: 5 }, "W", 2);
  addFurniture(furniture, "candle", 13, 0, { lit: true }, "S", 2);

  // Crystal balls at cardinal points of the outer ring
  addFurniture(furniture, "crystal_ball", 8, 1, {}, "S", 2);
  addFurniture(furniture, "crystal_ball", 8, 14, {}, "N", 2);
  addFurniture(furniture, "crystal_ball", 1, 8, {}, "E", 2);
  addFurniture(furniture, "crystal_ball", 14, 8, {}, "W", 2);

  // Candles on the bridges — marking the safe path
  addFurniture(furniture, "candle", 8, 5, { lit: true }, "S", 2);
  addFurniture(furniture, "candle", 8, 11, { lit: true }, "S", 2);
  addFurniture(furniture, "candle", 5, 8, { lit: true }, "S", 2);
  addFurniture(furniture, "candle", 11, 8, { lit: true }, "S", 2);

  return buildRoom("zone_crystal_caverns", "Crystal Caverns", W, H, tiles, walls, furniture, {
    warmth: 0.05, energy: 0.15, mystery: 1.0, nature: 0.1,
  });
}

function generateStormwatchCliffs(rng: () => number): Room {
  const W = 16, H = 14;
  const tiles: TileCell[][] = [];

  for (let y = 0; y < H; y++) {
    tiles[y] = [];
    for (let x = 0; x < W; x++) {
      if (y < 2) {
        // Deep sea — non-walkable water
        tiles[y][x] = makeTile("water", 0, false);
      } else if (y < 4) {
        // Narrow sandy beach — elevation 1
        tiles[y][x] = makeTile("sand", 1);
      } else if (y < 6) {
        // Cliff base — stone at elevation 2
        tiles[y][x] = makeTile("stone", 2);
      } else if (y < 9) {
        // Mid-cliff — stone at elevation 2-3
        tiles[y][x] = makeTile("stone", 2);
      } else {
        // Upper cliff plateau — elevation 3
        tiles[y][x] = makeTile("stone", 3);
      }
    }
  }

  // Winding switchback path from beach up the cliff
  // First switchback: beach (elev 1) to cliff base (elev 2) — right side
  for (let x = 10; x < 14; x++) {
    tiles[3][x] = makeTile("dirt", 1);
    tiles[4][x] = makeTile("dirt", 2);
  }
  // Second switchback: cliff base going left
  for (let x = 3; x < 11; x++) {
    tiles[5][x] = makeTile("stone", 2);
  }
  // Third switchback: mid-cliff ascending right
  for (let x = 5; x < 13; x++) {
    tiles[6][x] = makeTile("stone", 2);
  }
  for (let x = 10; x < 14; x++) {
    tiles[7][x] = makeTile("stone", 2);
    tiles[8][x] = makeTile("stone", 3);
  }
  // Final approach to upper plateau
  for (let x = 6; x < 14; x++) {
    tiles[8][x] = makeTile("stone", 3);
  }

  // Cliff face irregularity — some rocks jutting out (non-walkable)
  for (let i = 0; i < 4; i++) {
    const rx = Math.floor(rng() * (W - 4)) + 2;
    const ry = 5 + Math.floor(rng() * 3);
    if (tiles[ry][rx].walkable && tiles[ry][rx].type === "stone") {
      tiles[ry][rx].walkable = false;
      tiles[ry][rx].elevation = 3;
    }
  }

  // Lighthouse platform at the highest point (NW corner at elevation 3)
  tiles[9][1] = makeTile("stone", 3);
  tiles[9][2] = makeTile("stone", 3);
  tiles[10][1] = makeTile("stone", 3);
  tiles[10][2] = makeTile("stone", 3);
  // Dirt patches on the windswept plateau
  for (let y = 9; y < H; y++) {
    for (let x = 0; x < W; x++) {
      if (tiles[y][x].type === "stone" && tiles[y][x].walkable && rng() > 0.7) {
        tiles[y][x].type = "dirt";
      }
    }
  }

  // Tidal pools on the beach (small water patches)
  tiles[3][3] = makeTile("water", 1, false);
  tiles[3][4] = makeTile("water", 1, false);
  tiles[2][6] = makeTile("sand", 0, true); // sandbar

  // Ensure center (8,7) is walkable
  tiles[7][8] = makeTile("stone", 2);

  const walls: WallSegment[] = [];
  const furniture: FurniturePlacement[] = [];

  // Lighthouse at the high point
  addFurniture(furniture, "floor_lamp", 1, 9, { on: true, intensity: 1.0 }, "N", 3);
  addFurniture(furniture, "telescope", 2, 9, {}, "N", 3);
  addFurniture(furniture, "bookshelf", 1, 10, { books: 8 }, "E", 3);  // lighthouse keeper's log

  // Cliff-top campfire and rest area
  addFurniture(furniture, "fireplace", 8, 10, { lit: true }, "S", 3);
  addFurniture(furniture, "bench", 6, 10, {}, "E", 3);
  addFurniture(furniture, "bench", 10, 10, {}, "W", 3);

  // Weathered path markers
  addFurniture(furniture, "candle", 12, 4, { lit: true }, "S", 2);
  addFurniture(furniture, "candle", 5, 6, { lit: true }, "S", 2);
  addFurniture(furniture, "candle", 12, 8, { lit: true }, "S", 3);

  // Windswept vegetation — sparse, hardy plants
  addFurniture(furniture, "potted_plant", 5, 11, { growthStage: 1, waterLevel: 0.3 }, "S", 3);
  addFurniture(furniture, "potted_plant", 14, 12, { growthStage: 1, waterLevel: 0.2 }, "S", 3);

  // Beach driftwood (coffee table as debris)
  addFurniture(furniture, "coffee_table", 7, 3, {}, "S", 1);

  // Cliff edge lamps
  addFurniture(furniture, "floor_lamp", 0, 12, { on: true, intensity: 0.3 }, "NE", 3);
  addFurniture(furniture, "floor_lamp", 15, 12, { on: true, intensity: 0.3 }, "NW", 3);

  return buildRoom("zone_stormwatch_cliffs", "Stormwatch Cliffs", W, H, tiles, walls, furniture, {
    warmth: 0.0, energy: 0.95, mystery: 0.6, nature: 0.5,
  });
}

// ─── Room Builder ────────────────────────────────────────────────────────────

function buildRoom(
  id: string,
  name: string,
  width: number,
  height: number,
  tiles: TileCell[][],
  walls: WallSegment[],
  furniture: FurniturePlacement[],
  moodSeed: { warmth: number; energy: number; mystery: number; nature: number },
): Room {
  // Mark tiles under furniture as non-walkable
  for (const f of furniture) {
    if (f.tileX >= 0 && f.tileX < width && f.tileY >= 0 && f.tileY < height) {
      tiles[f.tileY][f.tileX].furniture = f.id;
      if (f.type !== "rug_round") {
        tiles[f.tileY][f.tileX].walkable = false;
      }
    }
  }

  return {
    id,
    name,
    ownerId: "world",
    width,
    height,
    tiles,
    walls,
    furniture,
    mood: {
      warmth: moodSeed.warmth,
      coziness: Math.min(1, moodSeed.warmth * 0.8 + 0.1),
      energy: moodSeed.energy,
      mystery: moodSeed.mystery,
      nature: moodSeed.nature,
      brightness: moodSeed.warmth > 0.3 ? 0.5 + moodSeed.warmth * 0.3 : 0.3,
      noise: moodSeed.energy * 0.5,
    },
  };
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Generate a Room that represents arriving at a WorldZone.
 * Each zone produces a unique, deterministic room based on zone.id.
 */
export function generateZoneRoom(zone: WorldZone): Room {
  _furnitureCounter = 0;
  const seed = hashStr(zone.id);
  const rng = seededRng(seed);

  switch (zone.id) {
    case "ember_lane":          return generateEmberLane(rng);
    case "hearthstone_square":  return generateHearthstoneSquare(rng);
    case "lantern_market":      return generateLanternMarket(rng);
    case "verdant_park":        return generateVerdantPark(rng);
    case "moonlit_harbor":      return generateMoonlitHarbor(rng);
    case "ironforge_district":  return generateIronforgeDistrict(rng);
    case "whisperwood":         return generateWhisperwood(rng);
    case "ashen_peaks":         return generateAshenPeaks(rng);
    case "roothollow":          return generateRoothollow(rng);
    case "skyreach_terrace":    return generateSkyreachTerrace(rng);
    case "crystal_caverns":     return generateCrystalCaverns(rng);
    case "stormwatch_cliffs":   return generateStormwatchCliffs(rng);
    case "training_grounds":    return generateTrainingGrounds(rng);
    default: {
      // Fallback — generic room based on zone type
      return generateFallbackRoom(zone, rng);
    }
  }
}

// ─── The Proving Grounds ─────────────────────────────────────────────────────
//
// 24×20 training arena. Layout:
//   - Top-left quadrant (0-11, 0-7):   Sand floor, scattered training furniture
//   - Center (8-15, 6-13):             Elevated stone ring — the dueling arena
//   - Right side (16-23, 0-9):         Training dummy yard (4 dummies on sand)
//   - Bottom-left (0-7, 12-19):        Spectator stands (stone benches, elevated)
//   - Bottom-right (16-23, 12-19):     Archery/ranged targets, obstacle course
//   - Entrances: south edge (x:10-13, y:19) cobblestone gateway
//
// The arena ring is elevated 1 tile above the sand floor with stone edges.
// Spectator benches are elevated 2 tiles for sightlines.
// Training dummies are placed as furniture markers — actual creatures spawned by getZoneCreatures.

function generateTrainingGrounds(rng: () => number): Room {
  const W = 24, H = 20;
  const tiles: TileCell[][] = [];

  // Base layer: packed sand everywhere
  for (let y = 0; y < H; y++) {
    tiles[y] = [];
    for (let x = 0; x < W; x++) {
      tiles[y][x] = makeTile("sand");
    }
  }

  // ── Central dueling arena — elevated stone ring (8-15, 6-13) ────────
  // Outer ring: cobblestone border at elevation 1
  for (let y = 6; y <= 13; y++) {
    for (let x = 8; x <= 15; x++) {
      const isEdge = x === 8 || x === 15 || y === 6 || y === 13;
      if (isEdge) {
        tiles[y][x] = makeTile("cobblestone", 1);
      } else {
        // Inner arena: stone floor at elevation 1
        tiles[y][x] = makeTile("stone", 1);
      }
    }
  }

  // Arena corners — decorative marble pillars (non-walkable)
  tiles[6][8] = makeTile("marble", 1, false);
  tiles[6][15] = makeTile("marble", 1, false);
  tiles[13][8] = makeTile("marble", 1, false);
  tiles[13][15] = makeTile("marble", 1, false);

  // ── Entrance ramp — south side approach ────────────────────────────
  for (let x = 10; x <= 13; x++) {
    tiles[18][x] = makeTile("cobblestone");
    tiles[19][x] = makeTile("cobblestone");
    tiles[17][x] = makeTile("cobblestone");
    tiles[16][x] = makeTile("cobblestone");
  }
  // Cobblestone path from entrance to arena
  for (let y = 14; y <= 16; y++) {
    tiles[y][11] = makeTile("cobblestone");
    tiles[y][12] = makeTile("cobblestone");
  }

  // ── Training dummy yard — right side (17-22, 2-8) ─────────────────
  // Packed dirt area for dummies
  for (let y = 2; y <= 8; y++) {
    for (let x = 17; x <= 22; x++) {
      tiles[y][x] = makeTile("dirt");
    }
  }

  // ── Spectator stands — bottom-left (1-6, 14-18) ──────────────────
  // Tiered stone seating, elevation rises from front (14) to back (18)
  for (let y = 14; y <= 18; y++) {
    for (let x = 1; x <= 6; x++) {
      const tier = y - 14;
      tiles[y][x] = makeTile("stone", tier, tier >= 3); // top rows are walkable stands
    }
  }
  // Front row always walkable (spectator seats)
  for (let x = 1; x <= 6; x++) {
    tiles[14][x].walkable = true;
    tiles[15][x].walkable = true;
  }

  // ── Archery range — bottom-right (17-22, 14-18) ──────────────────
  for (let y = 14; y <= 18; y++) {
    for (let x = 17; x <= 22; x++) {
      tiles[y][x] = makeTile("dirt");
    }
  }
  // Target wall at far end (non-walkable)
  for (let y = 14; y <= 16; y++) {
    tiles[y][22] = makeTile("stone", 0, false);
  }

  // ── Obstacle course — mid-left (1-6, 2-7) ────────────────────────
  // Grass patches with dirt path weaving through
  for (let y = 2; y <= 7; y++) {
    for (let x = 1; x <= 6; x++) {
      tiles[y][x] = makeTile("grass");
    }
  }
  // Dirt path through the obstacle course
  tiles[2][3] = makeTile("dirt"); tiles[3][3] = makeTile("dirt");
  tiles[3][4] = makeTile("dirt"); tiles[4][4] = makeTile("dirt");
  tiles[4][3] = makeTile("dirt"); tiles[5][2] = makeTile("dirt");
  tiles[5][3] = makeTile("dirt"); tiles[6][2] = makeTile("dirt");
  tiles[6][3] = makeTile("dirt"); tiles[7][3] = makeTile("dirt");

  // ── Connecting paths ──────────────────────────────────────────────
  // Path from entrance to dummy yard
  for (let x = 14; x <= 17; x++) {
    tiles[5][x] = makeTile("cobblestone");
  }
  // Path along south edge of arena to spectator stands
  for (let x = 7; x <= 10; x++) {
    tiles[14][x] = makeTile("cobblestone");
  }

  // ── Walls ─────────────────────────────────────────────────────────
  const walls: WallSegment[] = [];
  // Perimeter walls on north and west
  for (let x = 0; x < W; x++) {
    walls.push({ x, y: 0, side: "N", type: "stone", hasWindow: false, hasDoor: false });
  }
  for (let y = 0; y < H; y++) {
    walls.push({ x: 0, y, side: "W", type: "stone", hasWindow: false, hasDoor: false });
  }
  // Grand entrance gate on south wall
  for (let x = 0; x < W; x++) {
    const isDoor = x >= 10 && x <= 13;
    walls.push({ x, y: H - 1, side: "N", type: isDoor ? "ornate" : "stone", hasWindow: false, hasDoor: isDoor });
  }

  // ── Furniture ─────────────────────────────────────────────────────
  const furniture: FurniturePlacement[] = [];

  // Arena torches — corners of the dueling ring
  addFurniture(furniture, "floor_lamp", 9, 7, { on: true, intensity: 1.0 }, "SE");
  addFurniture(furniture, "floor_lamp", 14, 7, { on: true, intensity: 1.0 }, "SW");
  addFurniture(furniture, "floor_lamp", 9, 12, { on: true, intensity: 1.0 }, "NE");
  addFurniture(furniture, "floor_lamp", 14, 12, { on: true, intensity: 1.0 }, "NW");

  // Training weapon racks near dummy yard
  addFurniture(furniture, "bookshelf", 16, 2, { books: 0 }, "E"); // weapon rack (bookshelf placeholder)
  addFurniture(furniture, "bookshelf", 16, 5, { books: 0 }, "E"); // weapon rack

  // Spectator benches (sofas as bench stand-ins)
  addFurniture(furniture, "sofa", 2, 14, {}, "N");
  addFurniture(furniture, "sofa", 4, 14, {}, "N");
  addFurniture(furniture, "sofa", 2, 15, {}, "N");
  addFurniture(furniture, "sofa", 4, 15, {}, "N");

  // Entrance gate torches
  addFurniture(furniture, "floor_lamp", 10, 18, { on: true, intensity: 0.9 }, "N");
  addFurniture(furniture, "floor_lamp", 13, 18, { on: true, intensity: 0.9 }, "N");

  // Water barrel near obstacle course
  addFurniture(furniture, "fish_tank", 1, 1, {}, "SE"); // water barrel stand-in

  // Archery target markers
  addFurniture(furniture, "candle", 21, 14, {}, "W");
  addFurniture(furniture, "candle", 21, 15, {}, "W");
  addFurniture(furniture, "candle", 21, 16, {}, "W");

  // Arena center decoration — a rug marks the center of the ring
  addFurniture(furniture, "rug_round", 11, 9, {}, "S");
  addFurniture(furniture, "rug_round", 12, 10, {}, "S");

  // Random sand decorations
  if (rng() > 0.5) addFurniture(furniture, "potted_fern", 0, 9, { growthStage: 2 }, "SE");
  if (rng() > 0.5) addFurniture(furniture, "potted_plant", 7, 0, { growthStage: 3 }, "S");
  if (rng() > 0.5) addFurniture(furniture, "potted_fern", 23, 0, { growthStage: 1 }, "SW");

  return buildRoom("zone_training_grounds", "The Proving Grounds", W, H, tiles, walls, furniture, {
    warmth: 0.4, energy: 0.9, mystery: 0.1, nature: 0.15,
  });
}

function generateFallbackRoom(zone: WorldZone, rng: () => number): Room {
  const W = 10, H = 10;
  const tiles: TileCell[][] = [];
  const tileType: TileType = zone.type === "forest" || zone.type === "nature" ? "grass"
    : zone.type === "harbor" ? "dock"
    : zone.type === "underground" || zone.type === "danger" ? "stone_dark"
    : zone.type === "mountain" ? "stone"
    : "cobblestone";

  for (let y = 0; y < H; y++) {
    tiles[y] = [];
    for (let x = 0; x < W; x++) {
      tiles[y][x] = makeTile(tileType);
    }
  }

  const walls: WallSegment[] = [];
  const furniture: FurniturePlacement[] = [];
  addFurniture(furniture, "floor_lamp", 0, 0, { on: true, intensity: 0.5 }, "SE");

  void rng;
  return buildRoom(`zone_${zone.id}`, zone.name, W, H, tiles, walls, furniture, zone.defaultMood);
}

// ─── Zone Creature Spawning ──────────────────────────────────────────────────

/** Creature spawn descriptor — used by the game loop to create actual CreatureState instances */
export interface ZoneCreatureSpawn {
  speciesId: string;
  tileX: number;
  tileY: number;
  name?: string;
  palette?: Record<string, string>;
}

/**
 * Returns creature spawn data appropriate for a zone.
 * The game loop creates the actual CreatureState instances from these descriptors.
 */
export function getZoneCreatures(zone: WorldZone): ZoneCreatureSpawn[] {
  switch (zone.id) {
    case "ember_lane":
      // Neighborhood pets
      return [
        { speciesId: "cat_orange_tabby", tileX: 8, tileY: 3, name: "Marmalade" },
        { speciesId: "dog_corgi", tileX: 3, tileY: 7, name: "Biscuit" },
      ];

    case "hearthstone_square":
      // Town square — dogs and cats roaming
      return [
        { speciesId: "dog_labrador", tileX: 3, tileY: 8, name: "Copper" },
        { speciesId: "cat_house", tileX: 10, tileY: 4, name: "Whiskers" },
        { speciesId: "dog_mixed", tileX: 8, tileY: 11, name: "Scout" },
      ];

    case "lantern_market":
      // Market cats keeping mice at bay
      return [
        { speciesId: "cat_black", tileX: 4, tileY: 4, name: "Shadow" },
        { speciesId: "cat_siamese", tileX: 10, tileY: 8, name: "Pearl" },
      ];

    case "verdant_park":
      // Park animals — birds would be ideal but we have dogs at least
      return [
        { speciesId: "dog_shepherd", tileX: 10, tileY: 5, name: "Bramble" },
        { speciesId: "cat_house", tileX: 3, tileY: 12, name: "Clover" },
        { speciesId: "pony_shetland", tileX: 12, tileY: 9, name: "Buttercup" },
      ];

    case "moonlit_harbor":
      // Harbor cats, a working dog
      return [
        { speciesId: "cat_orange_tabby", tileX: 8, tileY: 8, name: "Anchor" },
        { speciesId: "dog_labrador", tileX: 5, tileY: 9, name: "Sailor" },
      ];

    case "ironforge_district":
      // Forge dog, tough
      return [
        { speciesId: "dog_shepherd", tileX: 9, tileY: 8, name: "Cinder" },
      ];

    case "whisperwood":
      // Wild wolves in the deep forest
      return [
        { speciesId: "wolf", tileX: 4, tileY: 6, name: "Grey Fang" },
        { speciesId: "wolf", tileX: 6, tileY: 8, name: "Shadow Runner" },
        { speciesId: "wolf", tileX: 3, tileY: 9, name: "Dusk" },
        { speciesId: "cat_black", tileX: 14, tileY: 4, name: "Phantom" },
      ];

    case "ashen_peaks":
      // Mountain wildlife — wolves, maybe a horse
      return [
        { speciesId: "arctic_wolf", tileX: 5, tileY: 10, name: "Frost" },
        { speciesId: "horse_white", tileX: 10, tileY: 12, name: "Snowmane" },
      ];

    case "roothollow":
      // Underground — sparse, mysterious
      return [
        { speciesId: "cat_black", tileX: 8, tileY: 3, name: "Echo" },
      ];

    case "skyreach_terrace":
      // High altitude — griffin or phoenix territory
      return [
        { speciesId: "griffin", tileX: 8, tileY: 2, name: "Zephyr" },
        { speciesId: "cat_siamese", tileX: 4, tileY: 8, name: "Nimbus" },
      ];

    case "crystal_caverns":
      // Dangerous — dragons lurk here
      return [
        { speciesId: "dragon_fire", tileX: 4, tileY: 4, name: "Ember" },
        { speciesId: "dragon_ice", tileX: 11, tileY: 11, name: "Glacius" },
      ];

    case "stormwatch_cliffs":
      // Storm-hardy creatures
      return [
        { speciesId: "dire_wolf", tileX: 6, tileY: 8, name: "Tempest" },
        { speciesId: "phoenix", tileX: 10, tileY: 7, name: "Stormfire" },
      ];

    case "training_grounds":
      // Training dummies — placed in the dummy yard, increasing difficulty
      return [
        { speciesId: "training_dummy_wooden",  tileX: 18, tileY: 3, name: "Wooden Dummy" },
        { speciesId: "training_dummy_straw",   tileX: 20, tileY: 3, name: "Straw Dummy" },
        { speciesId: "training_dummy_iron",    tileX: 18, tileY: 6, name: "Iron Dummy" },
        { speciesId: "training_dummy_armored", tileX: 20, tileY: 6, name: "Armored Dummy" },
      ];

    default:
      return [];
  }
}
